import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Scale, ShieldCheck, MapPin, FileText, Mail, Loader2, ArrowLeft, ChevronDown, ArrowUpRight, ArrowUp } from "lucide-react";
import { SimplyServedLogo } from "@/components/site/SimplyServedLogo";
import { CAPS, SHOTS } from "@/pages/site/SimplyServed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const errText = (d) => (typeof d === "string" ? d : Array.isArray(d) ? d.map((e) => e.msg).join(" ") : "Something went wrong. Please try again.");

function EmailCodeLogin() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [remember, setRemember] = useState(true);
  const go = (user) => navigate(user?.approved ? (user?.role === "server" ? "/today" : "/dashboard") : "/pending", { replace: true });

  const request = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await api.post("/auth/email/request", { email }); setStep("code"); }
    catch (err) { setError(errText(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/email/verify", { email, code, remember });
      setUser(data.user);
      go(data.user);
    } catch (err) { setError(errText(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (step === "code") {
    return (
      <form onSubmit={verify} className="space-y-3" data-testid="email-code-step">
        <div className="text-sm text-slate-300">We emailed a 6-digit code to <span className="font-semibold text-white">{email}</span>.</div>
        <Input data-testid="email-code-input" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6} placeholder="123456" value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="h-12 text-center text-2xl tracking-[0.5em] font-mono bg-slate-950 border-slate-700 text-white" />
        {error && <div className="text-xs text-rose-400" data-testid="email-login-error">{error}</div>}
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" data-testid="remember-device-label">
          <input type="checkbox" className="accent-blue-500 h-4 w-4" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-device" />
          Keep me signed in on this device for 30 days
        </label>
        <Button type="submit" disabled={busy || code.length !== 6} className="w-full h-11 bg-blue-600 hover:bg-blue-500 text-white font-semibold" data-testid="email-code-verify">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
        </Button>
        <div className="flex justify-between text-xs text-slate-400">
          <button type="button" className="hover:text-white inline-flex items-center gap-1" onClick={() => { setStep("email"); setCode(""); setError(""); }} data-testid="email-code-back"><ArrowLeft className="h-3 w-3" />Different email</button>
          <button type="button" className="hover:text-white" onClick={request} disabled={busy} data-testid="email-code-resend">Resend code</button>
        </div>
      </form>
    );
  }
  return (
    <form onSubmit={request} className="space-y-3" data-testid="email-request-step">
      <Input data-testid="email-login-input" type="email" required autoComplete="email" placeholder="you@yahoo.com, @hotmail.com, @comcast.net…" value={email}
        onChange={(e) => setEmail(e.target.value)} className="h-11 bg-slate-950 border-slate-700 text-white placeholder:text-slate-500" />
      {error && <div className="text-xs text-rose-400" data-testid="email-login-error">{error}</div>}
      <Button type="submit" disabled={busy || !email} variant="outline" className="w-full h-11 border-slate-600 bg-transparent text-white hover:bg-slate-800 font-semibold" data-testid="email-code-request">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-2" />Email me a sign-in code</>}
      </Button>
    </form>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [inviteState, setInviteState] = useState(() => (new URLSearchParams(window.location.search).get("invite") ? "working" : null));
  const [inviteError, setInviteError] = useState("");
  React.useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("invite");
    if (!token) return;
    api.post("/auth/invite/accept", { token })
      .then(({ data }) => { setUser(data.user); navigate(data.user?.role === "server" ? "/today" : "/dashboard", { replace: true }); })
      .catch((err) => { setInviteState("failed"); setInviteError(errText(err?.response?.data?.detail)); window.history.replaceState({}, "", "/login"); });
  }, [navigate, setUser]);
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div id="top" className="min-h-screen bg-slate-950 text-slate-100 relative overflow-hidden scroll-smooth">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.15),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(5,150,105,0.10),transparent_40%)]" />
      <div className="relative grid lg:grid-cols-2 min-h-screen">
        {/* Left brand pane */}
        <div className="hidden lg:flex flex-col justify-between p-12 border-r border-slate-800/70">
          <div className="flex items-center gap-3">
            <SimplyServedLogo className="h-28 xl:h-32 w-auto -ml-4 drop-shadow-[0_14px_30px_rgba(0,0,0,.5)]" />
          </div>

          <div className="space-y-8 max-w-md">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">The field. The court. One system.</div>
              <h1 className="text-4xl font-display font-bold leading-tight tracking-tight">
                Serve papers.<br />Log GPS proof.<br /><span className="text-blue-400">File affidavits.</span>
              </h1>
              <p className="mt-4 text-sm text-slate-400 leading-relaxed">
                Built in-house by InfoRetrieval for our own team and our partner process-serving companies. Every attempt, timestamped and defensible.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Feature icon={MapPin} title="GPS-Verified Attempts" desc="Coordinates + timestamp on every serve." />
              <Feature icon={FileText} title="Auto Affidavits" desc="Court-ready proof of service in seconds." />
              <Feature icon={ShieldCheck} title="Chain of Custody" desc="Verification hash on every document." />
              <Feature icon={Scale} title="Multi-State Forms" desc="DE • PA • NJ • MD court templates." />
            </div>
          </div>

          <div className="text-xs text-slate-500 font-mono flex items-center justify-between">
            <span>Simply Served • Built by InfoRetrieval, for InfoRetrieval and its partners</span>
            <a href="#about" className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1" data-testid="login-learn-more">What is this? <ChevronDown className="h-3 w-3" /></a>
          </div>
        </div>

        {/* Right auth pane */}
        <div className="flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <SimplyServedLogo className="h-20 w-auto -ml-3" />
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/70 backdrop-blur p-8">
              <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Sign in to continue</div>
              {inviteState === "working" && <div className="mt-4 rounded border border-blue-800 bg-blue-950/50 p-3 text-sm text-blue-200 flex items-center gap-2" data-testid="invite-working"><Loader2 className="h-4 w-4 animate-spin" />Signing you in from your invite…</div>}
              {inviteState === "failed" && <div className="mt-4 rounded border border-rose-800 bg-rose-950/40 p-3 text-sm text-rose-200" data-testid="invite-failed">{inviteError} Use the email code below instead.</div>}
              <h2 className="mt-2 text-2xl font-display font-bold">Welcome back.</h2>
              <p className="mt-2 text-sm text-slate-400">
                Sign in with Google, or get a one-time code sent to any email address. Access is limited to approved process servers and staff.
              </p>

              <Button
                data-testid="google-signin-button"
                onClick={handleGoogleLogin}
                className="w-full mt-8 h-11 bg-white text-slate-900 hover:bg-slate-100 font-semibold"
              >
                <GoogleG /> <span className="ml-2">Continue with Google</span>
              </Button>

              <div className="my-6 flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest text-slate-500"><span className="h-px flex-1 bg-slate-800" />or use any email<span className="h-px flex-1 bg-slate-800" /></div>
              <EmailCodeLogin />

              <div className="mt-6 text-xs text-slate-500 font-mono leading-relaxed">
                By continuing, you agree to the Simply Served Terms of Service and confirm you are an authorized user of your agency workspace.
              </div>
            </div>

            <div className="mt-6 text-center text-xs text-slate-500 font-mono">
              New accounts require approval from the agency owner before access is granted.
            </div>
            <div className="mt-3 text-center text-xs lg:hidden"><a href="#about" className="text-blue-400 hover:text-blue-300" data-testid="login-learn-more-mobile">What is Simply Served? ↓</a></div>
          </div>
        </div>
      </div>

      <AboutSimplyServed />
    </div>
  );
}

function AboutSimplyServed() {
  return (
    <section id="about" className="relative border-t border-slate-800/70" data-testid="login-about">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 space-y-20">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">Built by us · For us and our partners · For now</div>
            <h2 className="text-3xl sm:text-4xl font-display font-bold leading-tight tracking-tight">Simply Served is the software behind <span className="text-blue-400">InfoRetrieval</span>.</h2>
            <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">We're a woman-owned Delaware process service and investigations company serving attorneys since 1997. We couldn't find software that worked the way a Delaware investigator actually works — so we built our own. Simply Served runs every job we take: intake, GPS-verified attempts, live client trackers, court-ready affidavits, asset searches and record retrieval.</p>
            <p className="mt-3 text-sm sm:text-base text-slate-400 leading-relaxed">Today it's a private tool for our team and the partner process-serving companies we work with. Each partner gets its own fully separate workspace — their jobs, clients and servers stay theirs. One day it may open to others; for now, access is by approval only.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/home" className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 text-sm font-semibold" data-testid="login-about-site">Visit InfoRetrieval <ArrowUpRight className="h-4 w-4" /></Link>
              <a href="#partner" className="inline-flex items-center gap-2 rounded-full border border-slate-700 hover:border-slate-500 text-slate-200 px-5 py-2.5 text-sm font-semibold" data-testid="login-about-partner">Ask about partnering</a>
            </div>
          </div>
          <img src={SHOTS[0][0]} alt={SHOTS[0][1]} loading="lazy" className="w-full rounded-xl border border-slate-800 shadow-[0_30px_80px_-30px_rgba(37,99,235,0.45)]" />
        </div>

        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">What it does</div>
          <h3 className="text-2xl font-display font-bold tracking-tight">Everything a serve needs — and nothing you'd have to explain to a judge.</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="login-about-caps">
            {CAPS.map(([I, t, d]) => <div key={t} className="rounded-lg border border-slate-800 bg-slate-900/40 p-5"><I className="h-5 w-5 text-blue-400" /><div className="mt-3 text-sm font-semibold text-slate-100">{t}</div><div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{d}</div></div>)}
          </div>
        </div>

        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">A look inside</div>
          <h3 className="text-2xl font-display font-bold tracking-tight">Screens from the platform.</h3>
          <p className="text-sm text-slate-400 mt-2">Shown with fictional demo data — no client or case information is ever displayed publicly.</p>
          <div className="grid md:grid-cols-2 gap-6 mt-8" data-testid="login-about-shots">
            {SHOTS.slice(1).map(([src, cap]) => <figure key={src}><img src={src} alt={cap} loading="lazy" className="w-full rounded-xl border border-slate-800" /><figcaption className="text-xs text-slate-400 mt-2 font-mono">{cap}</figcaption></figure>)}
          </div>
        </div>

        <PartnerForm />

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-8 flex flex-wrap items-center justify-between gap-6">
          <div><div className="text-xs font-mono uppercase tracking-widest text-slate-400">Already a partner?</div><div className="text-xl font-display font-bold mt-1">Sign in at the top of this page.</div><div className="text-sm text-slate-400 mt-1">Use the Google account or email address we approved for your company.</div></div>
          <a href="#top" className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 hover:bg-slate-100 px-5 py-2.5 text-sm font-semibold" data-testid="login-about-top">Back to sign in <ArrowUp className="h-4 w-4" /></a>
        </div>
        <div className="text-xs text-slate-500 font-mono text-center">© {new Date().getFullYear()} InfoRetrieval · Simply Served is a private platform. <Link to="/simply-served" className="text-blue-400 hover:text-blue-300">Read more on our website</Link>.</div>
      </div>
    </section>
  );
}

function PartnerForm() {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", job_type: "Partnership inquiry", message: "" });
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!f.name.trim() || !f.email.trim() || !f.company.trim()) return setErr("Name, email and company are required.");
    setState("busy");
    try { await api.post("/public/contact", f); setState("done"); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Something went wrong — please try again."); setState("idle"); }
  };
  const inCls = "w-full h-11 rounded-md bg-slate-900/60 border border-slate-700 focus:border-blue-500 focus:outline-none px-3 text-sm text-slate-100 placeholder:text-slate-500";
  return (
    <div id="partner" className="rounded-xl border border-slate-800 bg-slate-900/40 p-8 grid lg:grid-cols-[1fr_1.2fr] gap-8" data-testid="partner-form-section">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-3">Partner with us</div>
        <h3 className="text-2xl font-display font-bold tracking-tight">Run your process-serving company on Simply Served.</h3>
        <p className="mt-3 text-sm text-slate-400 leading-relaxed">We bring a small number of reputable process-serving companies onto the platform as partners. You get your own private workspace — your jobs, clients, servers and branding — plus the ability to hand jobs back and forth with us. Tell us about your company and we'll reach out.</p>
        <ul className="mt-4 space-y-1.5 text-sm text-slate-300">{["Separate, private workspace for your company", "Share overflow jobs with us (and vice versa)", "Your clients get your branding on trackers and affidavits", "Approval-based access — no public sign-ups"].map((t) => <li key={t} className="flex gap-2"><ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />{t}</li>)}</ul>
      </div>
      {state === "done" ? (
        <div className="rounded-lg border border-emerald-800/60 bg-emerald-950/30 p-6 self-center" data-testid="partner-form-done"><div className="text-lg font-semibold text-emerald-300">Thanks — we've got it.</div><p className="text-sm text-slate-300 mt-1">We'll review your note and reach out within a business day or two.</p></div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="partner-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className={inCls} placeholder="Your name *" value={f.name} onChange={set("name")} data-testid="partner-name" />
            <input className={inCls} type="email" placeholder="Email *" value={f.email} onChange={set("email")} data-testid="partner-email" />
            <input className={inCls} placeholder="Company *" value={f.company} onChange={set("company")} data-testid="partner-company" />
            <input className={inCls} placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="partner-phone" />
          </div>
          <textarea className={`${inCls} h-auto py-2.5`} rows={4} placeholder="Tell us about your company — where you serve, how many jobs a month, what you're using today." value={f.message} onChange={set("message")} data-testid="partner-message" />
          {err && <div className="text-sm text-rose-400" data-testid="partner-error">{err}</div>}
          <Button type="submit" disabled={state === "busy"} className="h-11 px-6 bg-blue-600 hover:bg-blue-500 text-white font-semibold" data-testid="partner-submit">{state === "busy" ? "Sending…" : "Send partnership inquiry"}</Button>
        </form>
      )}
    </div>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
      <Icon className="h-4 w-4 text-blue-400" />
      <div className="mt-2 text-sm font-semibold text-slate-100">{title}</div>
      <div className="text-xs text-slate-400 mt-1 leading-relaxed">{desc}</div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.29 1.48-1.13 2.74-2.41 3.6v3h3.9c2.28-2.1 3.56-5.19 3.56-8.84z"/>
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.9l-3.9-3c-1.08.72-2.45 1.16-4.03 1.16-3.1 0-5.72-2.09-6.66-4.9H1.31v3.08C3.29 21.31 7.31 24 12 24z"/>
      <path fill="#FBBC05" d="M5.34 14.36A7.04 7.04 0 0 1 4.96 12c0-.83.14-1.63.38-2.36V6.56H1.31A11.98 11.98 0 0 0 0 12c0 1.94.46 3.77 1.31 5.44l4.03-3.08z"/>
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.16 15.24 0 12 0 7.31 0 3.29 2.69 1.31 6.56l4.03 3.08C6.28 6.84 8.9 4.75 12 4.75z"/>
    </svg>
  );
}
